import React, { useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { Button, IconButton } from "react-native-paper";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { NavigationProp } from "@react-navigation/native";
import { CommonActions, useNavigation } from "@react-navigation/native";
import type { Room } from "../types/types";
import {
  deleteRoom,
  removeParticipantFromRoom,
} from "../services/roomsService";
import { auth } from "../firebase";
import { useNow } from "../hooks/useNow";
import { useAppSelector } from "../redux/store";
import { formatRoomStart, isRoomActive } from "../utils/timeUtils";
import type RootStackParamList from "../navigation/RootStackParamList";

type Props = NativeStackScreenProps<RootStackParamList, "MeetingJoin">;

export default function JoinForMeetingModal({ route }: Props) {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { width } = useWindowDimensions();
  const room: Room = route.params.room;

  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const currentUserId = auth.currentUser?.uid;
  const isRoomCreator = currentUserId && room.createdBy === currentUserId;

  useNow();
  const currentTime = useAppSelector((state) => state.time.now);
  const canJoinRoom = isRoomActive(room.openDoorAt, currentTime);
  // e.g. "today, 3:55 PM" — doors open 5 minutes before the start (#142).
  const doorsOpenAt = formatRoomStart(
    room.openDoorAt,
    new Date(currentTime),
  ).replace(/^(Today|Tomorrow|Yesterday)/, (day) => day.toLowerCase());

  const handleClose = () => {
    navigation.goBack();
  };

  const handleEditRoom = () => {
    if (!room.id) return;
    if (!isRoomCreator) return;
    navigation.navigate("EditMeeting", { room });
  };

  const handleJoinMeeting = async () => {
    if (!room.id) return;

    try {
      setIsProcessing(true);
      // MeetingJoin is presented as a transparentModal, and on iOS every screen
      // pushed after a modal is rendered *inside* that modal's sheet — so the
      // whole call flow (Etiquette -> Call -> PostCall) appeared as
      // swipe-dismissable iOS cards revealing the screens underneath (#97).
      // StackActions.replace only swaps the JS route; iOS keeps the already
      // presented native modal container, so the flow stayed inside the sheet.
      // reset() rebuilds the native stack from scratch as [MeetingsTabs,
      // Etiquette], dropping the modal entirely, so the call flow presents as
      // full-screen cards.
      navigation.dispatch(
        CommonActions.reset({
          index: 1,
          routes: [
            { name: "MeetingsTabs" },
            {
              name: "Etiquette",
              params: {
                roomId: room.id,
                roomName: room.name,
                onContinue: () => {
                  navigation.navigate("Call", {
                    room,
                  });
                },
              },
            },
          ],
        }),
      );
    } catch (error) {
      console.error("Failed to join the meeting:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelMeeting = async () => {
    if (!room.id) return;

    try {
      setIsDeleting(true);
      const success = await deleteRoom(room.id);
      navigation.goBack();
      if (!success) {
        console.error("Failed to delete room");
      }
    } catch (error) {
      console.error("Error deleting room:", error);
      navigation.goBack();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLeaveRoom = async () => {
    if (!room.id || !currentUserId) return;

    try {
      setIsLeaving(true);
      const success = await removeParticipantFromRoom(room.id, currentUserId);
      navigation.goBack();
      if (!success) {
        console.error("Failed to leave room");
      }
    } catch (error) {
      console.error("Error leaving room:", error);
      navigation.goBack();
    } finally {
      setIsLeaving(false);
    }
  };

  return (
    <View style={styles.root}>
      <Pressable style={styles.backdrop} onPress={handleClose}>
        <View style={styles.centered} pointerEvents="box-none">
          <Pressable
            style={[styles.card, { width: Math.min(width - 32, 469) }]}
            testID="meeting-join-card"
            onPress={() => {}}
          >
            <ScrollView
              bounces={false}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.cardContent}
            >
              <View style={styles.textContainer}>
                <View style={styles.header}>
                  <Text style={styles.title}>Join Conversation</Text>
                </View>
                <IconButton
                  icon="close"
                  size={20}
                  onPress={handleClose}
                  accessibilityLabel="Close"
                  testID="meeting-join-close"
                  style={styles.closeButton}
                />

                <Text style={styles.subtitle}>
                  {room?.name ? `Room: ${room.name}` : "Room: Unnamed room"}
                </Text>
                <Text style={styles.description}>
                  You are about to join this meeting.
                </Text>
                {!canJoinRoom && (
                  <Text style={styles.warningText}>
                    Doors open {doorsOpenAt}, 5 minutes before the start. You
                    can join from then.
                  </Text>
                )}
              </View>

              <View style={styles.actions}>
                {isRoomCreator ? (
                  <>
                    <Button
                      mode="outlined"
                      onPress={handleEditRoom}
                      disabled={isProcessing || isDeleting || isLeaving}
                      buttonColor="transparent"
                      textColor="#3A6FB1"
                      style={styles.secondaryButton}
                      contentStyle={styles.secondaryButtonContent}
                      labelStyle={styles.secondaryButtonLabel}
                    >
                      Edit Room
                    </Button>
                    <Button
                      mode="outlined"
                      onPress={handleCancelMeeting}
                      disabled={isProcessing || isDeleting}
                      buttonColor="transparent"
                      textColor="#ef4444"
                      style={styles.dangerButton}
                      contentStyle={styles.secondaryButtonContent}
                      labelStyle={styles.secondaryButtonLabel}
                    >
                      {isDeleting ? (
                        <ActivityIndicator size="small" color="#ef4444" />
                      ) : (
                        "Delete Room"
                      )}
                    </Button>
                  </>
                ) : (
                  <Button
                    mode="outlined"
                    onPress={handleLeaveRoom}
                    disabled={isProcessing || isLeaving}
                    buttonColor="transparent"
                    textColor="#ef4444"
                    style={styles.dangerButton}
                    contentStyle={styles.secondaryButtonContent}
                    labelStyle={styles.secondaryButtonLabel}
                  >
                    {isLeaving ? (
                      <ActivityIndicator size="small" color="#ef4444" />
                    ) : (
                      "Remove me"
                    )}
                  </Button>
                )}

                <Button
                  mode="contained"
                  onPress={handleJoinMeeting}
                  disabled={
                    isProcessing || isDeleting || isLeaving || !canJoinRoom
                  }
                  buttonColor="#3A6FB1"
                  textColor="#fff"
                  // The blue background/label colours in these styles override
                  // Paper's disabled look, so before the doors open the button
                  // stayed blue and looked clickable (#142). Grey it out.
                  style={[
                    styles.primaryButton,
                    !canJoinRoom && styles.primaryButtonDisabled,
                  ]}
                  contentStyle={styles.primaryButtonContent}
                  labelStyle={[
                    styles.primaryButtonLabel,
                    !canJoinRoom && styles.primaryButtonLabelDisabled,
                  ]}
                >
                  {isProcessing ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : canJoinRoom ? (
                    "Join Conversation"
                  ) : (
                    `Opens ${doorsOpenAt}`
                  )}
                </Button>
              </View>
            </ScrollView>
          </Pressable>
        </View>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "transparent",
  },
  backdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 16,
    maxWidth: 469,
    maxHeight: "85%",
    borderWidth: 1,
    borderColor: "#d8dbe1",
    elevation: 6,
    position: "relative",
    overflow: "hidden",
  },
  cardContent: {
    paddingTop: 16,
    paddingRight: 16,
    paddingBottom: 32,
    paddingLeft: 16,
    justifyContent: "space-between",
    flexGrow: 1,
  },
  textContainer: {
    alignItems: "flex-start",
  },
  divider: {
    width: "100%",
    height: 3,
    borderRadius: 100,
    backgroundColor: "#D8DBE1",
  },
  header: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  title: {
    fontSize: 26,
    fontWeight: "700",
    lineHeight: 28,
    fontFamily: "Lexend",
    color: "#2b2b2f",
    textAlign: "left",
    flex: 1,
  },
  closeButton: {
    position: "absolute",
    top: 6,
    right: 6,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: "700",
    lineHeight: 18,
    fontFamily: "Lexend",
    color: "#6E6E6E",
    alignSelf: "flex-start",
    marginBottom: 4,
  },
  description: {
    fontSize: 14,
    lineHeight: 18,
    color: "#6E6E6E",
    fontFamily: "Lexend",
    marginBottom: 8,
  },
  warningText: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 16,
    color: "#b91c1c",
    fontWeight: "600",
  },
  actions: {
    flexDirection: "column",
    justifyContent: "center",
    gap: 12,
    width: "100%",
  },
  secondaryButton: {
    width: "100%",
    height: 54,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#3A6FB1",
  },
  dangerButton: {
    width: "100%",
    height: 54,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#ef4444",
  },
  secondaryButtonContent: {
    height: 54,
    paddingHorizontal: 20,
  },
  secondaryButtonLabel: {
    fontSize: 14,
    fontWeight: "700",
    letterSpacing: 0.4,
    fontFamily: "Lexend",
  },
  primaryButton: {
    width: "100%",
    height: 62,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#3A6FB1",
    backgroundColor: "#3A6FB1",
  },
  primaryButtonContent: {
    height: 62,
    paddingHorizontal: 20,
  },
  primaryButtonLabel: {
    fontSize: 14,
    fontWeight: "700",
    letterSpacing: 0.6,
    color: "#F6F7F9",
    fontFamily: "Lexend",
  },
  primaryButtonDisabled: {
    borderColor: "#D0D4DA",
    backgroundColor: "#D0D4DA",
  },
  primaryButtonLabelDisabled: {
    color: "#6E6E6E",
  },
});
